import { type ApolloClient, gql } from "@apollo/client";
import { EpocusStudyRoutingStatus } from "../../../../__generated__/base.gql";
import {
  EpocusStudiesSinceDocument,
  type EpocusStudiesSinceQuery,
  type EpocusStudiesSinceQueryVariables,
} from "./__generated__/routing-watcher.gql";
import type { WatchOutcome } from "./onboarding-chat";

const PATIENCE_MS: Record<WatchOutcome, number> = {
  ROUTED: 0,
  UNROUTED: 0,
  RECEIVED_NOT_PROCESSED: 30_000,
  NOT_RECEIVED: 600_000,
};

export type RoutingObservation = {
  outcome?: WatchOutcome;
  detail?: string;
};

export type RoutingWatcher = {
  poll(params: {
    programId: string;
    since: string;
    attempt: number;
    elapsedMs: number;
    signal: AbortSignal;
  }): Promise<RoutingObservation>;
};

export const createMockRoutingWatcher = (plan: {
  attempts: number;
  outcome: WatchOutcome;
  detail?: string;
}): RoutingWatcher => ({
  poll: ({ attempt }) =>
    Promise.resolve(
      attempt >= plan.attempts
        ? { outcome: plan.outcome, detail: plan.detail }
        : {},
    ),
});

gql`
  query EpocusStudiesSince($since: DateTime!, $programId: ID) {
    epocusStudiesSince(since: $since, programId: $programId) {
      status
      receivedAt
      ingressGateId
      ingressRouteKey
      programId
      programName
    }
  }
`;

type StudyRouting = EpocusStudiesSinceQuery["epocusStudiesSince"][number];

const OUTCOME_BY_STATUS: Record<EpocusStudyRoutingStatus, WatchOutcome> = {
  ROUTED: "ROUTED",
  UNROUTED: "UNROUTED",
  RECEIVED_NOT_PROCESSED: "RECEIVED_NOT_PROCESSED",
  NOT_RECEIVED: "NOT_RECEIVED",
};

const describeRouting = (
  outcome: WatchOutcome,
  routing: StudyRouting,
): string | undefined => {
  switch (outcome) {
    case "ROUTED":
      return routing.programName
        ? `Routed to ${routing.programName}.`
        : undefined;
    case "UNROUTED":
      return routing.ingressRouteKey
        ? `Study presented route key '${routing.ingressRouteKey}'${
            routing.ingressGateId ? ` on gate ${routing.ingressGateId}` : ""
          } — no ingress route matches it.`
        : undefined;
    case "RECEIVED_NOT_PROCESSED":
      return routing.receivedAt
        ? `Received at ${new Date(routing.receivedAt).toLocaleTimeString(
            "en-GB",
          )}, still unprocessed.`
        : undefined;
    case "NOT_RECEIVED":
      return undefined;
  }
};

export const createApolloRoutingWatcher = (
  client: ApolloClient<unknown>,
): RoutingWatcher => ({
  poll: async ({ programId, since, elapsedMs, signal }) => {
    const { data } = await client.query<
      EpocusStudiesSinceQuery,
      EpocusStudiesSinceQueryVariables
    >({
      query: EpocusStudiesSinceDocument,
      variables: { since, programId },
      fetchPolicy: "network-only",
      context: { fetchOptions: { signal } },
    });

    const studies = data.epocusStudiesSince;
    const routing =
      studies.find(
        (study) =>
          study.status === EpocusStudyRoutingStatus.Routed &&
          study.programId === programId,
      ) ??
      studies.find(
        (study) =>
          study.status === EpocusStudyRoutingStatus.Unrouted ||
          study.status === EpocusStudyRoutingStatus.ReceivedNotProcessed,
      );
    const outcome = routing
      ? OUTCOME_BY_STATUS[routing.status]
      : "NOT_RECEIVED";

    return elapsedMs >= PATIENCE_MS[outcome]
      ? { outcome, detail: routing && describeRouting(outcome, routing) }
      : {};
  },
});
